import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { Link, useNavigate } from "react-router-dom";

export const AdminManageStaticSpeedWorkoutTemplate = () => {
  const { staff } = useStaffAuthContext();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchTemplates = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response = await fetch(`${API_URL.MANAGE_STATIC_WORKOUT_TEMPLATES}?page=${page}&limit=${limit}`, {
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Failed to fetch templates");
        }

        setTemplates(data.data);
        setFilteredTemplates(data.data); // Initialize filteredTemplates with all data
        setTotalPages(data.totalPages);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTemplates();
  }, [page, staff.token]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;

    try {
      const response = await fetch(`${API_URL.DELETE_STATIC_WORKOUT_TEMPLATE}${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${staff.token}`,
        },
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to delete template");
      }
      setTemplates(templates.filter((template) => template._id !== id));
      setFilteredTemplates(filteredTemplates.filter((template) => template._id !== id));
    } catch (error) {
      setError(error.message);
    }
  };

  const filterData = (query) => {
    if (query.trim() === "") {
      setFilteredTemplates(templates);
    } else {
      const lowerQuery = query.toLowerCase();
      const filtered = templates.filter(
        (template) =>
          template.title.toLowerCase().includes(lowerQuery) ||
          (template.status && template.status.toLowerCase().includes(lowerQuery))
      );
      setFilteredTemplates(filtered);
    }
  };

  useEffect(() => {
    filterData(search);
  }, [search, templates]);

  return (
    <div className="flex h-screen">
      <StaffSideMenu />
      <div className="flex-1 p-6 bg-gray-100 pt-16 flex flex-col">
        <div className="flex justify-between mb-4">
          <input
            type="text"
            placeholder="Search by title"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="p-2 border rounded"
          />
          <div className="flex space-x-4">
            <Link
              to="/administrator/static-speed-workout/create-template"
              className="inline-flex items-center px-4 py-2 bg-blue-500 text-white font-semibold rounded-md shadow hover:bg-blue-500"
            >
              Create Static Speed Template
            </Link>
          </div>
        </div>
        <h1 className="text-2xl font-bold mb-4">Manage Static Speed Workout Templates</h1>
        {error && <div className="bg-red-100 text-red-500 p-3 rounded mb-4">{error}</div>}
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <div className="flex-1 overflow-auto border rounded shadow bg-white">
            <table className="table-auto w-full">
              <thead className="sticky top-0 bg-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left">Title</th>
                  <th className="px-4 py-2 text-left">Price</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                  <th className="px-4 py-2 text-left">Build Workout</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((template, index) => (
                  <tr
                    key={template._id}
                    className={index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"}
                  >
                    <td className="px-4 py-2">{template.title}</td>
                    <td className="px-4 py-2">${template.price}</td>
                    <td className="px-4 py-2">{template.status}</td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => navigate(`/administrator/static-speed-workout/edit-template/${template._id}`)}
                        className="mr-2 px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(template._id)}
                        className="mr-2 px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => navigate(`/administrator/static-speed-workout/create/${template._id}`)}
                        className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Build Workout
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-between mt-4">
              <button
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
